import { Link } from '@tanstack/react-router';
import { Check } from 'lucide-react';

import { t } from '../../../lib/i18n';
import { UserAvatar } from '../../clients/components/user-avatar';
import { initialsOf } from '../../clients/utils';
import { type AdminBid, isProcessedOnAuction } from '../types';
import {
  bidderIsEmail,
  bidderLabel,
  cn,
  formatAmount,
  formatClock,
  formatDayMonth,
  lotSiteHref,
} from '../utils';
import { BidStatusBadge } from './bid-status-badge';
import { LotThumbnail } from './lot-thumbnail';
import { RelayStatusBadge } from './relay-status-badge';

/**
 * The seven columns the artboard prints, in its order and at its widths
 * (`100px 1.4fr 1.4fr 110px 130px 130px 190px`, min 1040px before the section
 * scrolls). Кept as one string so the header and every row cannot drift apart.
 */
const GRID = 'lg:grid lg:grid-cols-[100px_minmax(0,1.4fr)_minmax(0,1.4fr)_110px_130px_130px_190px]';

const COLUMNS = [
  { key: 'date', align: '' },
  { key: 'client', align: '' },
  { key: 'lot', align: '' },
  { key: 'amount', align: 'text-right' },
  { key: 'status', align: '' },
  { key: 'relay', align: '' },
  { key: 'actions', align: 'text-right' },
] as const;

/** Shared focus style for every one of the row's three distinct link targets. */
const LINK_FOCUS =
  'rounded-cf-sm outline-none focus-visible:ring-2 focus-visible:ring-cf-brand focus-visible:ring-offset-1';

/**
 * All-bids admin table — `Redesign-D-Admin{,-Mobile}`, screen «Ставки».
 *
 * One component, two shapes: the dense seven-column grid the desktop artboard
 * draws inside a single bordered card, and below `lg` the card list the mobile
 * artboard draws instead. Same fields, same actions, same order — a table that
 * merely scrolls sideways on a phone is unusable at the relay desk, which is
 * where this screen is actually worked.
 *
 * It is a grid rather than a `<table>` because the mobile branch has to restack
 * the same cells into a card, and a `<tr>` cannot become a flex column.
 */
export function BidsTable({ bids }: { bids: AdminBid[] }) {
  if (bids.length === 0) {
    return (
      <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-10 text-center">
        <p className="text-base font-semibold">{t('bids.dAdmin.empty')}</p>
        <p className="mt-1.5 text-[13px] text-cf-ink-subtle">{t('bids.dAdmin.emptyBody')}</p>
      </section>
    );
  }

  return (
    <>
      {/* ── Mobile: one card per bid ────────────────────────────────────── */}
      <div className="flex flex-col gap-2 lg:hidden">
        {bids.map((bid) => (
          <BidRow key={bid._id} bid={bid} />
        ))}
      </div>

      {/* ── Desktop: the artboard's bordered grid ───────────────────────── */}
      <section className="hidden overflow-x-auto rounded-cf-lg border border-cf-line bg-cf-surface lg:block">
        <div
          className={cn(
            GRID,
            'min-w-[1040px] gap-3.5 border-b border-cf-line-soft px-[18px] py-2.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase',
          )}
        >
          {COLUMNS.map((column) => (
            <span key={column.key} className={column.align}>
              {t(`bids.dAdmin.columns.${column.key}`)}
            </span>
          ))}
        </div>

        {bids.map((bid) => (
          <BidRow key={bid._id} bid={bid} />
        ))}
      </section>
    </>
  );
}

function BidRow({ bid }: { bid: AdminBid }) {
  const bidder = bidderLabel(bid);
  const emailIsDuplicate = bidderIsEmail(bid, bidder);
  /** The car on the auction site, in a new tab (the admin lot page is retired). */
  const lotHref = lotSiteHref({ carId: bid.carId, listingId: bid.carListingId });

  /**
   * Read-only «оброблено» (owner, 2026-10-26). The row used to toggle the flag
   * and offer «Виграно / Не виграно» — both duplicated the bid page's «Дії
   * оператора», where «Оброблено» is the mirror itself. Derived like there, so a
   * relayed bid never reads «не оброблено» next to «Передано на аукціон».
   */
  const { processed } = isProcessedOnAuction(bid);
  const processedStatus = (
    <span
      data-testid="bids-row-processed"
      className={cn(
        'relative z-[1] inline-flex items-center gap-1 font-mono text-[11px] whitespace-nowrap',
        processed ? 'text-cf-success' : 'text-cf-ink-subtle',
      )}
    >
      {processed && <Check size={11} strokeWidth={2.5} aria-hidden="true" />}
      {t(processed ? 'bids.dAdmin.processedOn' : 'bids.dAdmin.notProcessed')}
    </span>
  );

  return (
    <>
      {/* ── Mobile card ─────────────────────────────────────────────────── */}
      {/*
       * `relative` + the bid-details Link's `after:` pseudo-element below give
       * the WHOLE card one click/Enter target, without nesting it inside the
       * client link, the lot link, or the relay/processed buttons that also
       * live in this card (nested interactive elements break screen readers).
       * Those three stay real, independently-focusable controls; `relative
       * z-[1]` lifts each one above the stretched overlay so it keeps getting
       * its own clicks instead of the card's.
       */}
      <article className="relative flex flex-col gap-2 rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-3 lg:hidden">
        <div className="flex items-start justify-between gap-2">
          <Link
            to="/clients/$id"
            params={{ id: bid.userId }}
            aria-label={t('bids.dAdmin.openClientAria', { bidder })}
            className={cn(LINK_FOCUS, 'relative z-[1] flex min-w-0 items-center gap-2')}
          >
            <UserAvatar
              photoURL={bid.user?.photoURL}
              initials={initialsOf(bidder)}
              alt=""
              className="h-9 w-9 shrink-0 text-[12px]"
            />
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold">{bidder}</span>
              {!emailIsDuplicate && (
                <span className="block truncate text-xs text-cf-ink-subtle">
                  {bid.user?.email ?? '—'}
                </span>
              )}
            </span>
          </Link>

          {/*
           * The card-wide navigation target — see the desktop row's comment
           * for why this is a plain (non-`relative`) anchor whose `::after`
           * stretches to the nearest positioned ancestor (the `article`
           * above) instead of wrapping the client link / lot link / buttons.
           */}
          <Link
            to="/bids/$id"
            params={{ id: bid._id }}
            aria-label={t('bids.dAdmin.detailAria', { bidder })}
            className={cn(
              LINK_FOCUS,
              "shrink-0 text-right after:absolute after:inset-0 after:content-['']",
            )}
          >
            <div className="font-mono text-[15px] font-medium whitespace-nowrap">
              CHF {formatAmount(bid.bid)}
            </div>
            <div className="font-mono text-[11px] text-cf-ink-subtle">
              {formatDayMonth(bid.createdAt)}
            </div>
          </Link>
        </div>

        <a
          href={lotHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('bids.dAdmin.openLotAria', { lot: lotRef(bid) })}
          className={cn(LINK_FOCUS, 'relative z-[1] flex min-w-0 items-center gap-2')}
        >
          <LotThumbnail src={bid.carThumbnailUrl} />
          <span className={cn('truncate text-xs text-cf-ink-muted', lotRefClass(bid))}>
            {lotRef(bid)}
          </span>
        </a>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <BidStatusBadge status={bid.status} />
            {bid.relayStatus && <RelayStatusBadge relayStatus={bid.relayStatus} />}
          </div>
          <div className="flex items-center gap-1.5">{processedStatus}</div>
        </div>
      </article>

      {/* ── Desktop row ─────────────────────────────────────────────────── */}
      <div
        className={cn(
          GRID,
          'relative hidden min-w-[1040px] items-center gap-3.5 border-b border-cf-line-soft px-[18px] py-3 text-[13px] last:border-b-0 hover:bg-cf-surface-sunken lg:grid',
        )}
      >
        {/*
         * The row-wide navigation target. It is a plain, statically-positioned
         * anchor whose `::after` is stretched to `inset-0` of the nearest
         * POSITIONED ancestor — this row (`relative` above) — so the click/
         * focus/Enter target covers the entire row while the anchor's own box
         * stays tiny (the date text). That keeps it a SIBLING of the client
         * link, the lot link and the action buttons rather than a wrapper
         * around them, so no interactive element ends up nested inside
         * another. Cmd/Ctrl/middle-click all work — it is a real `<a>`.
         */}
        <Link
          to="/bids/$id"
          params={{ id: bid._id }}
          aria-label={t('bids.dAdmin.detailAria', { bidder })}
          className={cn(LINK_FOCUS, "font-mono text-xs text-cf-ink-muted after:absolute after:inset-0 after:content-['']")}
        >
          <div>{formatDayMonth(bid.createdAt)}</div>
          <div className="text-cf-ink-subtle">{formatClock(bid.createdAt)}</div>
        </Link>

        <Link
          to="/clients/$id"
          params={{ id: bid.userId }}
          aria-label={t('bids.dAdmin.openClientAria', { bidder })}
          className={cn(LINK_FOCUS, 'relative z-[1] flex min-w-0 items-center gap-2')}
        >
          <UserAvatar
            photoURL={bid.user?.photoURL}
            initials={initialsOf(bidder)}
            alt=""
            className="h-8 w-8 shrink-0 text-[11px]"
          />
          <span className="min-w-0">
            <span className="block truncate font-semibold">{bidder}</span>
            {!emailIsDuplicate && (
              <span className="block truncate text-xs text-cf-ink-subtle">
                {bid.user?.email ?? '—'}
              </span>
            )}
          </span>
        </Link>

        <a
          href={lotHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('bids.dAdmin.openLotAria', { lot: lotRef(bid) })}
          className={cn(LINK_FOCUS, 'relative z-[1] flex min-w-0 items-center gap-2')}
        >
          <LotThumbnail src={bid.carThumbnailUrl} />
          <span className="min-w-0">
            <span className={cn('block truncate text-xs', lotRefClass(bid))}>{lotRef(bid)}</span>
            {bid.tenderCloseAt && (
              <span className="block truncate text-xs text-cf-ink-subtle">
                {formatShortClose(bid.tenderCloseAt)}
              </span>
            )}
          </span>
        </a>

        <span className="text-right font-mono text-sm font-medium whitespace-nowrap">
          CHF {formatAmount(bid.bid)}
        </span>

        <BidStatusBadge status={bid.status} />

        <div className="relative z-[1] flex min-w-0 flex-col items-start gap-1">
          {bid.relayStatus ? (
            <RelayStatusBadge relayStatus={bid.relayStatus} />
          ) : (
            <span className="font-mono text-[11px] text-cf-ink-subtle">
              {t('bids.dAdmin.noRelay')}
            </span>
          )}
          {processedStatus}
        </div>

        {/* «Дії» stays as a column; the decisions live on the bid page. */}
        <div />
      </div>

    </>
  );
}

/** How many trailing characters of a raw id still tell two lots apart in a row. */
const SHORT_ID_LENGTH = 6;

/**
 * «VW CALIFORNIA BEACH», else the listing id, else «…d1a7bb».
 *
 * The artboard prints «Лот» as the car. GET /bids?view=standard projects
 * `carTitle` («brand model») from the same `alpicar_cars` $lookup as the
 * thumbnail (apps/api bid-read.helper); until 2026-10-07 this cell printed the
 * raw Mongo `carId` instead, which told the operator nothing. The same
 * precedence as the client page's bids tab (`carLabel` in client-bids-tab.tsx).
 */
function lotRef(bid: AdminBid): string {
  if (bid.carTitle) return bid.carTitle;
  if (bid.carListingId) return bid.carListingId;
  return `…${bid.carId.slice(-SHORT_ID_LENGTH)}`;
}

/** A title reads as prose; a bare reference reads as code. */
function lotRefClass(bid: AdminBid): string {
  return bid.carTitle ? 'font-medium' : 'font-mono';
}

/** «закривається 14.03 · 16:30» — only rendered when the bid carries a close. */
function formatShortClose(value: string): string {
  const day = formatDayMonth(value);
  const clock = formatClock(value);
  return clock ? `${day} · ${clock}` : day;
}
